// CLEAN, STABLE LEDGER LIST PAGE
import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import LedgerSummaryStrip from "./components/LedgerSummaryStrip";
import LedgerTable from "./components/LedgerTable";
import useLedgerData from "./hooks/useLedgerData";
import "../Ledger.css";
import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { OrderRecord } from "../types";
import api from "src/features/auth/axiosConfig";
import { useUser } from "src/business-types/grocery/components/UserContext";

// Interface for paginated navigation
interface PaginatedNavigation {
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  totalRecords: number;
}

const Ledger: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const preserved = location.state || null;
  const urlPage = parseInt(searchParams.get("pageNumber") || "1", 10) - 1;
  const urlPageSize = parseInt(searchParams.get("pageSize") || "10", 10);
  const urlSearchTerm = searchParams.get("searchBy") || "";
  const [currentPage, setCurrentPage] = useState(urlPage);
  const { userConfigration } = useUser();
  const [rowsPerPage, setRowsPerPage] = useState(userConfigration?.tableDefultSize || urlPageSize);
  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [showZeroOutstanding, setShowZeroOutstanding] = useState(true);


  useEffect(() => {
    document.title = "Ledger List › iMetanic";
    if (preserved?.fromPage) {
      const params = new URLSearchParams();

      params.set("pageNumber", preserved.fromPage);
      params.set("pageSize", preserved.fromPageSize);
      if (preserved.searchTerm) params.set("searchBy", preserved.searchTerm);


      setCurrentPage(preserved.fromPage - 1);
      setRowsPerPage(preserved.fromPageSize);
      setSearchTerm(preserved.searchTerm || "");

      setSearchParams(params, { replace: true });

      navigate(location.pathname, { replace: true });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("pageNumber", String(currentPage + 1));
    params.set("pageSize", String(rowsPerPage));

    if (searchTerm.trim() !== "") {
      params.set("searchBy", searchTerm.trim());
    }

    setSearchParams(params, { replace: true });
  }, [currentPage, rowsPerPage, searchTerm]);

  const [allRecords, setAllRecords] = useState<OrderRecord[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);

  // Fetch current page records
  const fetchCurrentPageRecords = async (search: string, pageNum: number = currentPage,) => {
    setLoadingAll(true);
    try {
      const response = await api.get("/pos/ledger", {
        params: {
          pageNumber: pageNum + 1,
          pageSize: rowsPerPage,
          searchBy: search,
        },
      });
      const data = response.data.result || [];
      setAllRecords(data);
      return data;
    } finally {
      setLoadingAll(false);
    }
  };

  useEffect(() => {
    fetchCurrentPageRecords(searchTerm);
  }, [searchTerm, currentPage, rowsPerPage]);

const { ledgerRecords, totalOutstanding, totalRecords, formatCurrency, loading } = useLedgerData(currentPage, rowsPerPage, searchTerm , showZeroOutstanding);

  const handleRowClick = async (id: string) => {
    const currentPageData = await fetchCurrentPageRecords(searchTerm);

    // Create pagination info for navigation
    const paginationInfo: PaginatedNavigation = {
      currentPage: currentPage + 1,
      pageSize: rowsPerPage,
      searchTerm,
      totalRecords,
    };

    navigate(`/v1/ledger/${id}`, {
      state: {
        customers: currentPageData, // Current page data
        paginationInfo: paginationInfo, // Pagination details for fetching more
        searchTerm,
        fromPage: currentPage + 1,
        fromPageSize: rowsPerPage,
      },
    });
  };

  return (
    <div className='flex h-[91.5vh] flex-col flex-1'>
      <PageTitle title='- Ledger List' />

      <div className='ledger-wrapper'>
        <LedgerSummaryStrip
          totalRecords={totalRecords}
          totalOutstanding={formatCurrency(totalOutstanding)}
          searchTerm={searchTerm}
          onSearchChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(0);
          }}
          showZeroOutstanding={showZeroOutstanding}
          onToggleZeroOutstanding={() => {
            setShowZeroOutstanding(!showZeroOutstanding);
            setCurrentPage(0); // reset pagination when toggled
          }}
          />


        <div className='ledger-table-container'>
          <LedgerTable
            data={ledgerRecords}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            totalRecords={totalRecords}
            formatCurrency={formatCurrency}
            onRowClick={handleRowClick}
            onPageChange={(p) => setCurrentPage(p)}
            onRowsPerPageChange={(v) => {
              setRowsPerPage(v);
              setCurrentPage(0);
            }}
          />
        </div>
      </div>

      {(loading || loadingAll) && <LoadingScreen />}
    </div>
  );
};

export default Ledger;
